import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  CheckCircle2,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Info,
  Loader2,
  Paperclip,
  Send,
  Sparkles,
  Trash2,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import axiosInstance from '../../api/axios';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import {
  clearEngagementMessages,
  createEngagement,
  deleteEngagementDocument,
  downloadEngagementReport,
  getEngagement,
  getEngagementResults,
  listEngagementsForControl,
  runEngagementAnalysis,
  sendEngagementChat,
  updateEngagement,
  uploadEngagementDocument,
} from '../../api/aiEngagements';

const DOCUMENT_CATEGORIES = [
  { value: 'evidence', label: 'Control Evidence' },
  { value: 'population', label: 'Population' },
  { value: 'ipe', label: 'IPE' },
  { value: 'walkthrough', label: 'Walkthrough' },
  { value: 'prior_year', label: 'Prior Year Workpaper' },
  { value: 'support', label: 'Supporting Doc' },
  { value: 'screenshot', label: 'Screenshot' },
];

const CONTEXT_FIELDS = [
  { key: 'assumptions', label: 'Testing assumptions' },
  { key: 'sampling_methodology', label: 'Sampling methodology' },
  { key: 'materiality', label: 'Materiality considerations' },
  { key: 'instructions', label: 'Specific testing instructions' },
  { key: 'known_exceptions', label: 'Known exceptions' },
  { key: 'control_nuances', label: 'Control nuances' },
  { key: 'focus_areas', label: 'Areas requiring additional focus' },
];

const ENGAGEMENT_STAGES = [
  { key: 'understanding', label: 'Understand' },
  { key: 'plan_proposed', label: 'Plan' },
  { key: 'collecting', label: 'Collect' },
  { key: 'testing', label: 'Test' },
  { key: 'review', label: 'Review' },
  { key: 'reporting', label: 'Report' },
];

// 'ready' sits between Collect and Test; map it to Collect-complete.
function stageIndex(stage) {
  if (stage === 'ready') return 3;
  if (stage === 'concluded') return 6;
  const idx = ENGAGEMENT_STAGES.findIndex((s) => s.key === stage);
  return idx === -1 ? 0 : idx;
}

function StageStepper({ stage }) {
  const current = stageIndex(stage);
  return (
    <div className="flex items-center gap-1 px-6 py-2 bg-white border-b border-gray-200 overflow-x-auto">
      {ENGAGEMENT_STAGES.map((s, i) => {
        const done = current > i;
        const active = current === i;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <div className={`h-px w-6 flex-shrink-0 ${done || active ? 'bg-purple-300' : 'bg-gray-200'}`} />}
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full flex-shrink-0 text-xs font-medium ${
              active ? 'bg-purple-100 text-purple-800'
                : done ? 'text-purple-600'
                  : 'text-gray-400'
            }`}>
              <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[10px] ${
                done ? 'bg-purple-600 text-white' : active ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {done ? '✓' : i + 1}
              </span>
              {s.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const STATUS_STYLES = {
  passed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
  skipped: 'bg-gray-100 text-gray-600',
};

function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
      {status || 'unknown'}
    </span>
  );
}

function docIcon(filename) {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileSpreadsheet className="h-4 w-4 text-green-600" />;
  return <FileText className="h-4 w-4 text-red-600" />;
}

// ---------------------------------------------------------------------------
// Chat bubbles
// ---------------------------------------------------------------------------

function SystemEventBubble({ content }) {
  return (
    <div className="text-center">
      <span className="inline-block px-3 py-1 text-xs text-gray-500 bg-gray-100 rounded-full">{content}</span>
    </div>
  );
}

function UserBubble({ content }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 text-white px-4 py-2.5 text-sm whitespace-pre-wrap">
        {content}
      </div>
    </div>
  );
}

// Styled markdown for assistant replies (no typography plugin installed, so map
// elements explicitly). Keeps headings/lists/tables scannable inside bubbles.
const markdownComponents = {
  h1: ({ children }) => <h3 className="text-sm font-bold text-gray-900 mt-4 mb-1.5 first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="text-sm font-bold text-gray-900 mt-4 mb-1.5 first:mt-0">{children}</h3>,
  h3: ({ children }) => (
    <h3 className="text-[13px] font-bold text-purple-900 uppercase tracking-wide mt-4 mb-1.5 first:mt-0 pb-1 border-b border-purple-100">{children}</h3>
  ),
  h4: ({ children }) => <h4 className="text-sm font-semibold text-gray-900 mt-3 mb-1 first:mt-0">{children}</h4>,
  p: (props) => <p className="my-1.5 leading-relaxed first:mt-0 last:mb-0" {...props} />,
  ul: (props) => <ul className="my-1.5 pl-5 list-disc space-y-1" {...props} />,
  ol: (props) => <ol className="my-1.5 pl-5 list-decimal space-y-1" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  strong: (props) => <strong className="font-semibold text-gray-900" {...props} />,
  hr: () => <hr className="my-3 border-gray-200" />,
  blockquote: (props) => (
    <blockquote className="border-l-2 border-purple-200 pl-3 my-2 text-gray-600 italic" {...props} />
  ),
  code: ({ inline, ...props }) => (
    <code className="px-1 py-0.5 bg-gray-100 rounded text-[12px] font-mono text-gray-800" {...props} />
  ),
  table: (props) => (
    <div className="my-2 overflow-x-auto">
      <table className="text-xs border-collapse w-full" {...props} />
    </div>
  ),
  thead: (props) => <thead className="bg-gray-50" {...props} />,
  th: (props) => <th className="border border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700" {...props} />,
  td: (props) => <td className="border border-gray-200 px-2 py-1.5 align-top" {...props} />,
};

function ArtifactRequestCard({ artifact, onUpload, busy }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState('');
  return (
    <div className="mt-1 rounded-xl border-2 border-amber-200 bg-amber-50/60 px-4 py-3">
      <div className="flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-amber-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Requested: {artifact.name}</p>
          {artifact.reason && <p className="text-xs text-gray-600 mt-0.5">{artifact.reason}</p>}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.png,.jpg,.jpeg"
          onChange={async (e) => {
            const files = Array.from(e.target.files).map((file) => ({ file, category: artifact.category || 'evidence' }));
            e.target.value = '';
            if (!files.length) return;
            setUploading(true);
            try {
              await onUpload(files, { note: note.trim() });
              setNote('');
            } finally { setUploading(false); }
          }}
        />
        <button
          type="button"
          disabled={busy || uploading}
          onClick={() => inputRef.current?.click()}
          className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5 flex-shrink-0"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          Upload {artifact.category ? `(${artifact.category})` : ''}
        </button>
      </div>
      <input
        type="text"
        value={note}
        disabled={busy || uploading}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add context for the AI (optional) — e.g. which tab matters, known quirks..."
        className="mt-2.5 w-full px-3 py-1.5 text-xs border border-amber-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:bg-gray-50"
      />
    </div>
  );
}

function AssistantBubble({ content, meta, busy, onSendText, onPrefill, onUpload, onDownloadReport }) {
  const versionChip = meta?.result_version;
  const responseType = meta?.response_type;
  const isProposedPlan = responseType === 'proposed_test_plan';
  const isControlReview = responseType === 'control_summary';
  const artifact = meta?.requested_artifact;
  return (
    <div className="flex gap-2.5">
      <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Bot className="h-4 w-4 text-purple-600" />
      </div>
      <div className="max-w-[85%] space-y-1.5">
        <div className={`rounded-2xl rounded-tl-sm bg-white border px-4 py-3 text-sm text-gray-800 ${
          isProposedPlan ? 'border-purple-300 border-2' : 'border-gray-200'
        }`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>
        </div>
        {(versionChip || responseType === 'needs_documents' || isProposedPlan || isControlReview) && (
          <div className="flex flex-wrap items-center gap-2">
            {isControlReview && onSendText && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSendText('Confirmed — the control wording and objective are accurate as reviewed. Proceed.')}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-purple-600 rounded-full hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  <CheckCircle2 className="h-3 w-3" /> Confirm & continue
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onPrefill?.('Correction to the control review: ')}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-purple-700 bg-white border border-purple-300 rounded-full hover:bg-purple-50 disabled:opacity-50 transition-colors"
                >
                  Correct...
                </button>
              </>
            )}
            {versionChip && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-purple-50 text-purple-700 rounded-full border border-purple-200">
                <Sparkles className="h-3 w-3" /> Assessment revised → v{versionChip}
              </span>
            )}
            {responseType === 'needs_documents' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-700 rounded-full border border-amber-200">
                <Paperclip className="h-3 w-3" /> Additional evidence needed
              </span>
            )}
            {isProposedPlan && (
              <>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-purple-50 text-purple-700 rounded-full border border-purple-200">
                  <CheckCircle2 className="h-3 w-3" /> Test plan proposed
                </span>
                {onSendText && (
                  <>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onSendText('Approved — proceed with this plan.')}
                      className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-white bg-purple-600 rounded-full hover:bg-purple-700 disabled:opacity-50 transition-colors"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Approve
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onPrefill?.('Modify the plan: ')}
                      className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-purple-700 bg-white border border-purple-300 rounded-full hover:bg-purple-50 disabled:opacity-50 transition-colors"
                    >
                      Modify
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onSendText('Regenerate the test plan with a meaningfully different approach.')}
                      className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-purple-700 bg-white border border-purple-300 rounded-full hover:bg-purple-50 disabled:opacity-50 transition-colors"
                    >
                      ↻ Regenerate
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
        {artifact && onUpload && (
          <ArtifactRequestCard artifact={artifact} onUpload={onUpload} busy={busy} />
        )}
        {Array.isArray(meta?.recommended_reports) && meta.recommended_reports.length > 0 && onDownloadReport && (
          <div className="mt-1 space-y-1.5">
            {meta.recommended_reports.map((rep) => (
              <div key={rep.key} className="flex items-center gap-2 rounded-xl border border-purple-200 bg-purple-50/50 px-3.5 py-2.5">
                <FileText className="h-4 w-4 text-purple-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{rep.name}</p>
                  <p className="text-xs text-gray-500">{rep.reason}</p>
                </div>
                {(rep.key === 'working_papers' ? ['pdf', 'xlsx'] : ['docx']).map((fmt) => (
                  <button
                    key={fmt}
                    type="button"
                    disabled={busy}
                    onClick={() => onDownloadReport(rep.key, fmt)}
                    className="px-2.5 py-1 text-xs font-medium text-purple-700 bg-white border border-purple-300 rounded-full hover:bg-purple-100 disabled:opacity-50 flex-shrink-0 uppercase"
                  >
                    {fmt}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
        {Array.isArray(meta?.review_items) && meta.review_items.length > 0 && onSendText && (
          <div className="mt-1 space-y-2">
            {meta.review_items.map((item) => (
              <div key={item.item_id} className={`rounded-xl border-2 px-4 py-3 ${
                item.kind === 'exception' ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'
              }`}>
                <div className="flex items-start gap-2">
                  {item.kind === 'exception'
                    ? <XCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    : <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {item.item_id}{item.record_ref ? ` · ${item.record_ref}` : ''} <span className="font-normal text-gray-500">(step {item.step_number})</span>
                    </p>
                    <p className="text-xs text-gray-700 mt-0.5">{item.summary}</p>
                    <p className="text-xs text-gray-500 mt-1 italic">Recommendation: {item.recommendation}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-2.5">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onSendText(`Confirm ${item.item_id}: I agree with this finding as reported.`)}
                    className="px-3 py-1 text-xs font-medium text-white bg-gray-800 rounded-full hover:bg-gray-900 disabled:opacity-50"
                  >
                    Confirm finding
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onPrefill?.(`Waive ${item.item_id} because `)}
                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 disabled:opacity-50"
                  >
                    Waive...
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const THINKING_PHRASES = [
  'Reading through the evidence...',
  'Cross-referencing the samples...',
  'Checking approval dates and signers...',
  'Reviewing the population data...',
  'Tracing amounts back to source documents...',
  'Weighing the findings...',
  'Drafting a grounded response...',
];

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 ml-0.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 bg-purple-400 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.18}s`, animationDuration: '0.9s' }}
        />
      ))}
    </span>
  );
}

function ProgressBubble({ messages }) {
  const latest = messages[messages.length - 1];
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const lastMessageRef = useRef(null);

  // Rotate placeholder phrases; whenever a fresh real status arrives, show it
  // for a few seconds before falling back to the rotating "thinking" text.
  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIdx((i) => (i + 1) % THINKING_PHRASES.length);
      setShowPlaceholder(true);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (latest?.message && latest.message !== lastMessageRef.current) {
      lastMessageRef.current = latest.message;
      setShowPlaceholder(false);
    }
  }, [latest?.message]);

  const displayText = !showPlaceholder && latest?.message
    ? latest.message
    : THINKING_PHRASES[phraseIdx];

  return (
    <div className="flex gap-2.5">
      <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Bot className="h-4 w-4 text-purple-600 animate-pulse" />
      </div>
      <div className="rounded-2xl rounded-tl-sm bg-white border border-gray-200 px-4 py-3 min-w-[260px]">
        <div className="flex items-center text-sm text-gray-500">
          <span className="animate-pulse">{displayText}</span>
          <TypingDots />
        </div>
      </div>
    </div>
  );
}

function AssessmentCardBubble({ result, versionNumber, onOpenDetails }) {
  const w = result?.walkthrough || {};
  const icon = w.overall_status === 'passed'
    ? <CheckCircle2 className="h-5 w-5 text-green-600" />
    : w.overall_status === 'failed'
      ? <XCircle className="h-5 w-5 text-red-600" />
      : <AlertTriangle className="h-5 w-5 text-amber-500" />;
  return (
    <div className="flex gap-2.5">
      <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Sparkles className="h-4 w-4 text-purple-600" />
      </div>
      <button
        type="button"
        onClick={onOpenDetails}
        className="max-w-[85%] text-left rounded-2xl rounded-tl-sm bg-white border-2 border-purple-200 px-4 py-3 hover:border-purple-400 transition-colors"
      >
        <div className="flex items-center gap-2 mb-1.5">
          {icon}
          <span className="text-sm font-semibold text-gray-900 capitalize">
            Assessment v{versionNumber}: control {w.overall_status}
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          {result.steps_passed} passed · {result.steps_failed} failed · {result.steps_warning} warnings
          {' · '}{result.total_exceptions} exception(s) · confidence {Math.round((w.confidence_score || 0) * 100)}%
        </p>
        <p className="text-sm text-gray-700 line-clamp-3">{w.executive_summary}</p>
        <span className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-purple-600">
          View full assessment <ChevronRight className="h-3 w-3" />
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Composer with attachments
// ---------------------------------------------------------------------------

function ChatComposer({ disabled, busy, onSend, onUpload, quickActions, prefill, onPrefillConsumed }) {
  const [text, setText] = useState('');
  const [pending, setPending] = useState([]); // [{file, category}]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (prefill) {
      setText(prefill);
      textareaRef.current?.focus();
      onPrefillConsumed?.();
    }
  }, [prefill, onPrefillConsumed]);

  const handleSend = async () => {
    const message = text.trim();
    if (pending.length > 0) {
      setUploading(true);
      try {
        // silent: a typed message accompanies the upload, so the agent gets
        // context from it — no synthetic acknowledgment turn needed.
        await onUpload(pending, { silent: Boolean(message) });
        setPending([]);
      } finally {
        setUploading(false);
      }
    }
    if (message) {
      setText('');
      await onSend(message);
    }
  };

  const canSend = !disabled && !busy && !uploading && (text.trim() || pending.length > 0);

  return (
    <div className="border-t border-gray-200 bg-white px-4 pt-3 pb-4">
      {quickActions?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2.5">
          {quickActions.map((qa) => (
            <button
              key={qa.label}
              type="button"
              disabled={busy || disabled}
              onClick={qa.onClick}
              className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-full hover:bg-purple-100 disabled:opacity-50 transition-colors"
            >
              {qa.label}
            </button>
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2.5">
          {pending.map((p, i) => (
            <div key={i} className="flex items-center gap-2 pl-2.5 pr-1.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs">
              {docIcon(p.file.name)}
              <span className="max-w-[160px] truncate">{p.file.name}</span>
              <select
                value={p.category}
                onChange={(e) => setPending((prev) => prev.map((x, xi) => xi === i ? { ...x, category: e.target.value } : x))}
                className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white"
              >
                {DOCUMENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <button type="button" onClick={() => setPending((prev) => prev.filter((_, xi) => xi !== i))} className="p-0.5 text-gray-400 hover:text-red-500">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.png,.jpg,.jpeg"
          onChange={(e) => {
            const files = Array.from(e.target.files).map((file) => ({ file, category: 'evidence' }));
            setPending((prev) => [...prev, ...files]);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => fileInputRef.current?.click()}
          className="p-2.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          title="Attach evidence, populations, IPE..."
        >
          <Paperclip className="h-5 w-5" />
        </button>
        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          disabled={disabled || busy}
          placeholder={busy ? 'Working on it...' : 'Ask a question, challenge a conclusion, or give an instruction...'}
          onChange={(e) => {
            setText(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (canSend) void handleSend();
            }
          }}
          className="flex-1 px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 disabled:bg-gray-50"
        />
        <button
          type="button"
          disabled={!canSend}
          onClick={() => void handleSend()}
          className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          {busy || uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Side drawer: Documents / Context / Assessment detail
// ---------------------------------------------------------------------------

function DrawerDocuments({ engagementId, documents, onChanged }) {
  const handleDelete = async (docId) => {
    try {
      await deleteEngagementDocument(engagementId, docId);
      onChanged();
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, 'Failed to remove document'));
    }
  };
  return (
    <div className="space-y-2">
      {documents.map((doc) => (
        <div key={doc.id} className="flex items-center gap-2.5 px-3 py-2.5 bg-white border border-gray-200 rounded-lg">
          {docIcon(doc.filename)}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-900 truncate">{doc.filename}</p>
            <p className="text-xs text-gray-400">{DOCUMENT_CATEGORIES.find((c) => c.value === doc.category)?.label || doc.category}</p>
          </div>
          <button type="button" onClick={() => handleDelete(doc.id)} className="p-1 text-gray-400 hover:text-red-600 rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {documents.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Attach files via the paperclip in the chat</p>}
    </div>
  );
}

function DrawerContext({ engagement, onSaved }) {
  const [values, setValues] = useState(engagement?.testing_context || {});
  const [periodStart, setPeriodStart] = useState(engagement?.testing_period_start || '');
  const [periodEnd, setPeriodEnd] = useState(engagement?.testing_period_end || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(engagement?.testing_context || {});
    setPeriodStart(engagement?.testing_period_start || '');
    setPeriodEnd(engagement?.testing_period_end || '');
  }, [engagement]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateEngagement(engagement.id, {
        testing_context: values,
        testing_period_start: periodStart || undefined,
        testing_period_end: periodEnd || undefined,
      });
      toast.success('Context saved');
      onSaved();
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, 'Failed to save context'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Period start</label>
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Period end</label>
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md" />
        </div>
      </div>
      {CONTEXT_FIELDS.map((field) => (
        <div key={field.key}>
          <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
          <textarea
            rows={2}
            value={values[field.key] || ''}
            onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md resize-y"
          />
        </div>
      ))}
      <button
        type="button" onClick={handleSave} disabled={saving}
        className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Context
      </button>
    </div>
  );
}

function DrawerAssessment({ results, selectedVersion, onSelectVersion }) {
  const selected = results.find((r) => r.version_number === selectedVersion) || results[0];
  const result = selected?.result;
  if (!result) return <p className="text-sm text-gray-400 text-center py-6">No assessment yet</p>;
  const w = result.walkthrough || {};

  return (
    <div className="space-y-4">
      {results.length > 1 && (
        <select
          value={selected?.version_number}
          onChange={(e) => onSelectVersion(Number(e.target.value))}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
        >
          {results.map((r) => (
            <option key={r.version_number} value={r.version_number}>
              v{r.version_number} — {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
            </option>
          ))}
        </select>
      )}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <StatusBadge status={w.overall_status} />
          <span className="text-xs text-gray-500">confidence {Math.round((w.confidence_score || 0) * 100)}%</span>
        </div>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{w.executive_summary}</p>
        {w.key_findings?.length > 0 && (
          <ul className="mt-3 space-y-1">
            {w.key_findings.map((f, i) => (
              <li key={i} className="text-xs text-gray-600 flex gap-1.5">
                <Info className="h-3.5 w-3.5 text-blue-500 flex-shrink-0 mt-0.5" />{f}
              </li>
            ))}
          </ul>
        )}
        {w.recommendations?.length > 0 && (
          <ul className="mt-2 space-y-1">
            {w.recommendations.map((r, i) => (
              <li key={i} className="text-xs text-gray-600 flex gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-purple-500 flex-shrink-0 mt-0.5" />{r}
              </li>
            ))}
          </ul>
        )}
      </div>
      {(result.step_results || []).map((step) => (
        <details key={step.step_number} className="bg-white border border-gray-200 rounded-lg">
          <summary className="px-3.5 py-3 cursor-pointer flex items-center gap-2 select-none">
            <StatusBadge status={step.status} />
            <span className="text-xs font-medium text-gray-900 flex-1">
              Step {step.step_number}: {step.step_description?.slice(0, 70)}
            </span>
          </summary>
          <div className="px-3.5 pb-3 space-y-2 border-t border-gray-100 pt-2.5">
            <p className="text-xs text-gray-700">{step.detailed_findings || step.findings_summary}</p>
            <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
              {step.population_size != null && <span>Pop: {step.population_size}</span>}
              {step.sample_size != null && <span>Sample: {step.sample_size}</span>}
              <span>{step.samples_passed}✓ / {step.samples_failed}✗</span>
              {step.preparer_info && <span>Prep: {step.preparer_info}</span>}
              {step.reviewer_info && <span>Rev: {step.reviewer_info}</span>}
              <span>SoD: {step.segregation_verified ? '✓' : '—'}</span>
            </div>
            {step.exceptions?.length > 0 && (
              <div className="bg-red-50 rounded p-2">
                {step.exceptions.map((ex, i) => (
                  <p key={i} className="text-[11px] text-red-700">{ex.record_ref}: {ex.issue} ({ex.severity})</p>
                ))}
              </div>
            )}
            {step.evidence_citations?.length > 0 && (
              <p className="text-[11px] text-gray-500">
                <span className="font-medium">Evidence: </span>
                {step.evidence_citations.map((c, i) => (
                  <span key={i}>
                    {c.source_file}{c.sheet_name ? ` › ${c.sheet_name}` : ''}{c.page_number ? ` p.${c.page_number}` : ''}
                    {i < step.evidence_citations.length - 1 ? '; ' : ''}
                  </span>
                ))}
              </p>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const WELCOME_MESSAGE = (controlId) => `**Welcome to the AI testing engagement for control ${controlId}.**

Two ways to begin:
- **Tell me about this control first** — type any context below (testing focus, known issues, sampling preferences) and I'll fold it into the testing strategy.
- **Or jump straight in** — click *Analyze Control with AI* and I'll start by reviewing the control's wording and objective with you, then propose a test plan.

You can attach documents at any time with the paperclip.`;

export default function AIEngagementPage() {
  const { id: routeControlId } = useParams();
  const navigate = useNavigate();

  const [control, setControl] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false); // analysis or chat turn running
  const [progressMessages, setProgressMessages] = useState([]);
  const [localTurns, setLocalTurns] = useState([]); // optimistic user bubbles not yet on server
  const [drawerTab, setDrawerTab] = useState(null); // null | documents | context | assessment
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearingChat, setClearingChat] = useState(false);
  const [composerPrefill, setComposerPrefill] = useState('');
  const [pendingReview, setPendingReview] = useState(false);
  const bottomRef = useRef(null);

  const refreshEngagement = useCallback(async (engagementId) => {
    const detail = await getEngagement(engagementId);
    setEngagement(detail.engagement);
    setDocuments(detail.documents || []);
    setMessages(detail.messages || []);
    setLocalTurns([]);
    if (detail.results?.length) {
      const full = await getEngagementResults(engagementId);
      setResults(full);
      setSelectedVersion((v) => v ?? full[0]?.version_number ?? null);
    } else {
      setResults([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const controlResp = await axiosInstance.get(`/api/sox-controls/${routeControlId}`, {
          headers: { Authorization: localStorage.getItem('userHash') },
        });
        if (cancelled) return;
        const controlData = controlResp.data;
        setControl(controlData);

        const controlUuid = controlData.uuid || controlData.id || routeControlId;
        const engagements = await listEngagementsForControl(controlUuid);
        let target = engagements[0];
        if (!target) {
          target = await createEngagement({
            sox_control_id: controlUuid,
            title: `AI Testing — ${controlData.control_id || routeControlId}`,
          });
        }
        if (cancelled) return;
        await refreshEngagement(target.id);
      } catch (err) {
        if (!cancelled) {
          toast.error(resolve403ErrorMessage(err, 'Failed to load engagement'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [routeControlId, refreshEngagement]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, localTurns, progressMessages, busy]);

  const resultByVersion = useMemo(() => {
    const map = {};
    for (const r of results) map[r.version_number] = r;
    return map;
  }, [results]);

  const latestVersion = results[0]?.version_number ?? null;

  // Unguarded core so agent-driven transitions (ready_to_test) can chain into
  // a run right after a chat turn releases the busy flag.
  const runAnalysis = useCallback(async () => {
    if (!engagement) return;
    if (documents.length === 0) {
      toast.error('Attach at least one document first');
      return;
    }
    setBusy(true);
    setProgressMessages([{ message: 'Starting analysis...', progress: 1 }]);
    try {
      const { versionNumber } = await runEngagementAnalysis({
        engagementId: engagement.id,
        onProgress: (p) => setProgressMessages((prev) => [...prev.slice(-79), p]),
      });
      toast.success(versionNumber ? `Assessment v${versionNumber} ready` : 'Assessment complete');
      await refreshEngagement(engagement.id);
      if (versionNumber) setSelectedVersion(versionNumber);
      setPendingReview(true); // Phase B: agent presents the review queue next
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, err.message || 'Analysis failed'));
    } finally {
      setBusy(false);
      setProgressMessages([]);
    }
  }, [engagement, documents.length, refreshEngagement]);

  const handleRunAnalysis = useCallback(async () => {
    if (busy) return;
    await runAnalysis();
  }, [busy, runAnalysis]);

  // One chat turn. synthetic=true for app-generated directives ([BEGIN
  // ENGAGEMENT], [DOCUMENT UPLOADED...]) — no user bubble is shown or stored.
  const sendTurn = useCallback(async (message, { synthetic = false } = {}) => {
    if (!engagement) return;
    if (!synthetic) setLocalTurns((prev) => [...prev, { role: 'user', content: message }]);
    setBusy(true);
    setProgressMessages([{ message: synthetic ? 'Reviewing the engagement...' : 'Reviewing evidence and conversation...', progress: 5 }]);
    let startTest = false;
    try {
      const outcome = await sendEngagementChat({
        engagementId: engagement.id,
        message,
        onProgress: (p) => setProgressMessages((prev) => [...prev.slice(-79), p]),
      });
      startTest = outcome.responseType === 'rerun_requested' || outcome.responseType === 'ready_to_test';
      if (outcome.versionNumber) {
        toast.success(`Assessment revised — v${outcome.versionNumber}`);
        setSelectedVersion(outcome.versionNumber);
      }
      await refreshEngagement(engagement.id);
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, err.message || 'Chat turn failed'));
      await refreshEngagement(engagement.id);
    } finally {
      setBusy(false);
      setProgressMessages([]);
    }
    if (startTest) await runAnalysis();
  }, [engagement, refreshEngagement, runAnalysis]);

  const handleSendMessage = useCallback(async (message) => {
    if (busy) return;
    await sendTurn(message);
  }, [busy, sendTurn]);

  const handleDownloadReport = useCallback(async (reportKey, format) => {
    if (!engagement) return;
    const t = toast.loading(`Generating ${reportKey.replace(/_/g, ' ')} (${format.toUpperCase()})...`);
    try {
      await downloadEngagementReport(engagement.id, reportKey, format);
      toast.success('Report downloaded', { id: t });
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, getApiErrorMessage(err, 'Report generation failed')), { id: t });
    }
  }, [engagement]);

  const handleUpload = useCallback(async (pendingFiles, { silent = false, note = '' } = {}) => {
    if (!engagement) return;
    try {
      for (const { file, category } of pendingFiles) {
        await uploadEngagementDocument(engagement.id, file, category);
      }
      await refreshEngagement(engagement.id);
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, getApiErrorMessage(err, 'Upload failed')));
      throw err;
    }
    // Auto-acknowledge: the agent opens the new files and responds in-flow.
    // An auditor note rides along as context/instruction for the agent.
    if (!silent && !busy) {
      const listing = pendingFiles.map(({ file, category }) => `${file.name} (${category})`).join(', ');
      const suffix = note ? ` Auditor note: ${note}` : '';
      await sendTurn(`[DOCUMENT UPLOADED: ${listing}]${suffix}`, { synthetic: true });
    }
  }, [engagement, refreshEngagement, sendTurn, busy]);

  // Phase B: right after an analysis completes, the agent presents the
  // review queue (exceptions + low-confidence items) for disposition.
  useEffect(() => {
    if (!pendingReview || busy || !engagement) return;
    setPendingReview(false);
    void sendTurn('[REVIEW RESULTS]', { synthetic: true });
  }, [pendingReview, busy, engagement, sendTurn]);

  // Fresh engagements no longer auto-kickoff: the auditor chooses to either
  // type opening context (their message starts the flow) or click
  // "Analyze Control with AI" (triggers the standard kickoff).
  const conversationTurns = useMemo(
    () => messages.filter((m) => m.role !== 'system_event').length,
    [messages]
  );
  // Shown on fresh engagements AND after Clear Chat (which resets the stage
  // server-side) — past assessment versions may still exist in the drawer.
  const showStartOptions = !loading && Boolean(engagement) && !busy
    && conversationTurns === 0;

  const handleAnalyzeControl = useCallback(() => {
    void sendTurn('[BEGIN ENGAGEMENT]', { synthetic: true });
  }, [sendTurn]);

  const handleClearChat = useCallback(async () => {
    if (!engagement) return;
    setClearingChat(true);
    try {
      await clearEngagementMessages(engagement.id);
      setLocalTurns([]);
      setProgressMessages([]);
      await refreshEngagement(engagement.id);
      toast.success('Chat cleared');
      setShowClearConfirm(false);
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, 'Failed to clear chat'));
    } finally {
      setClearingChat(false);
    }
  }, [engagement, refreshEngagement]);

  const quickActions = useMemo(() => {
    const actions = [];
    if (results.length > 0 && !busy) {
      actions.push({ label: 'Explain the conclusion', onClick: () => handleSendMessage('Explain how you reached the overall conclusion, citing the specific evidence that drove it.') });
      actions.push({ label: 'What evidence is missing?', onClick: () => handleSendMessage('What documentation is missing or insufficient to fully test this control?') });
      actions.push({ label: '↻ Re-run analysis', onClick: handleRunAnalysis });
    }
    return actions;
  }, [results.length, busy, handleRunAnalysis, handleSendMessage]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-purple-500" />
      </div>
    );
  }

  // Build the chat timeline: welcome + server messages (+ assessment cards) + optimistic turns
  const timeline = [];
  timeline.push({ kind: 'assistant', content: WELCOME_MESSAGE(control?.control_id || ''), key: 'welcome' });
  messages.forEach((msg, i) => {
    if (msg.role === 'system_event') {
      timeline.push({ kind: 'system', content: msg.content, key: msg.id });
    } else if (msg.role === 'user') {
      timeline.push({ kind: 'user', content: msg.content, key: msg.id });
    } else {
      timeline.push({ kind: 'assistant', content: msg.content, meta: msg.metadata, key: msg.id });
      const version = msg.metadata?.result_version;
      if (version && resultByVersion[version]) {
        timeline.push({ kind: 'assessment', version, key: `${msg.id}-card` });
      }
    }
  });
  localTurns.forEach((t, i) => timeline.push({ kind: 'user', content: t.content, key: `local-${i}` }));

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(`/sox-controls/view/${control?.control_id || routeControlId}`)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
            <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base font-bold text-gray-900 truncate">AI Testing — {control?.control_id}</h1>
              <p className="text-xs text-gray-500 truncate">{control?.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowClearConfirm(true)}
              disabled={busy || messages.length === 0}
              className="px-3 py-1.5 text-xs font-medium rounded-md inline-flex items-center gap-1.5 text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors mr-1"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear Chat
            </button>
            {[
              { id: 'documents', label: `Documents (${documents.length})`, icon: FolderOpen },
              { id: 'context', label: 'Context', icon: Info },
              { id: 'assessment', label: latestVersion ? `Assessment v${latestVersion}` : 'Assessment', icon: CheckCircle2 },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setDrawerTab((t) => (t === id ? null : id))}
                className={`px-3 py-1.5 text-xs font-medium rounded-md inline-flex items-center gap-1.5 transition-colors ${
                  drawerTab === id ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Engagement stage stepper */}
      <StageStepper stage={engagement?.stage} />

      {/* Body: chat + optional drawer */}
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto px-4 py-6">
            <div className="max-w-3xl mx-auto space-y-4">
              {timeline.map((item) => {
                if (item.kind === 'system') return <SystemEventBubble key={item.key} content={item.content} />;
                if (item.kind === 'user') return <UserBubble key={item.key} content={item.content} />;
                if (item.kind === 'assessment') {
                  const r = resultByVersion[item.version];
                  return (
                    <AssessmentCardBubble
                      key={item.key}
                      result={r?.result}
                      versionNumber={item.version}
                      onOpenDetails={() => { setSelectedVersion(item.version); setDrawerTab('assessment'); }}
                    />
                  );
                }
                return (
                  <AssistantBubble
                    key={item.key}
                    content={item.content}
                    meta={item.meta}
                    busy={busy}
                    onSendText={(t) => { void handleSendMessage(t); }}
                    onPrefill={setComposerPrefill}
                    onUpload={handleUpload}
                    onDownloadReport={handleDownloadReport}
                  />
                );
              })}
              {showStartOptions && (
                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    onClick={handleAnalyzeControl}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-700 shadow-sm transition-colors"
                  >
                    <Sparkles className="h-4 w-4" />
                    Analyze Control with AI
                  </button>
                </div>
              )}
              {busy && <ProgressBubble messages={progressMessages} />}
              <div ref={bottomRef} />
            </div>
          </div>
          <ChatComposer
            disabled={!engagement}
            busy={busy}
            onSend={handleSendMessage}
            onUpload={handleUpload}
            quickActions={quickActions}
            prefill={composerPrefill}
            onPrefillConsumed={() => setComposerPrefill('')}
          />
        </div>

        {showClearConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => { if (!clearingChat) setShowClearConfirm(false); }}
          >
            <div
              className="w-full max-w-md mx-4 bg-white rounded-xl shadow-xl p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900">Clear this conversation?</h3>
                  <p className="mt-1.5 text-sm text-gray-600">
                    The engagement for <span className="font-medium">{control?.control_id}</span> restarts from
                    the beginning — back to the Understand stage, where you can provide context or
                    analyze the control again. Uploaded documents and past assessment versions are kept.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  disabled={clearingChat}
                  onClick={() => setShowClearConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={clearingChat}
                  onClick={() => { void handleClearChat(); }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {clearingChat && <Loader2 className="h-4 w-4 animate-spin" />}
                  Clear Chat
                </button>
              </div>
            </div>
          </div>
        )}

        {drawerTab && (
          <div className="w-[380px] border-l border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between sticky top-0">
              <h3 className="text-sm font-semibold text-gray-900 capitalize">{drawerTab}</h3>
              <button type="button" onClick={() => setDrawerTab(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4">
              {drawerTab === 'documents' && engagement && (
                <DrawerDocuments engagementId={engagement.id} documents={documents} onChanged={() => refreshEngagement(engagement.id)} />
              )}
              {drawerTab === 'context' && engagement && (
                <DrawerContext engagement={engagement} onSaved={() => refreshEngagement(engagement.id)} />
              )}
              {drawerTab === 'assessment' && (
                <DrawerAssessment results={results} selectedVersion={selectedVersion} onSelectVersion={setSelectedVersion} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
